/**
 * "Full tracks - standard" display mode: a single plain-coloured line per
 * track, using the *real* GPS route rather than the coarse start/end stub
 * that the bulk Tracks fetch returns (see store/layer getTrackFullTrace and
 * MapComponent.refreshFullTrackTraces).
 *
 * Kept as its own source/layer, separate from:
 *  - 'Tracks' (icons + stub line, centerpoints mode)
 *  - 'actualTracks' (colour-coded full mode, still a placeholder)
 * so each display mode owns its own geometry and none of them interfere.
 *
 * Data flow: the bulk fetch keeps the 'Tracks' entry in `mapLayers` populated
 * with one (usually stub) feature per visible track (see
 * MapComponent.getTracksMapLayerFeatures - trackLayer is a different piece of
 * state, only ever holding a single individually-selected track). Whenever
 * 'full' mode is active, MapComponent.refreshFullTrackTraces fetches the real
 * trace per track and stores it in the `fullTrackTraces` cache (keyed by
 * track id). This module merges the two: real trace where cached, stub as a
 * fallback while it's still loading.
 */

import { computeWithTrackCache, pruneTrackCache } from './trackSegmentCache';

export const TRACKS_FULL_STANDARD_SOURCE_ID = 'TracksFullStandard';
export const TRACKS_FULL_STANDARD_LAYER_ID = 'Tracks_lines';

/**
 * Adds the (initially empty) source + line layer, if not already present.
 * @param {string} [visibility] - 'visible' | 'none'.
 */
export function addTracksFullStandardLayer(visibility = 'none') {
  if (!this.map) return;

  if (!this.map.getSource(TRACKS_FULL_STANDARD_SOURCE_ID)) {
    this.map.addSource(TRACKS_FULL_STANDARD_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
  }

  if (!this.map.getLayer(TRACKS_FULL_STANDARD_LAYER_ID)) {
    this.map.addLayer({
      id: TRACKS_FULL_STANDARD_LAYER_ID,
      source: TRACKS_FULL_STANDARD_SOURCE_ID,
      type: 'line',
      layout: {
        visibility,
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#1f4ba8',
        'line-width': 2.5,
        'line-opacity': 0.9
      }
    });
  }
}

/**
 * A 'line' Mapbox layer can only render LineString/MultiLineString. Track
 * geometry (both the coarse stub and the real fetched trace) commonly
 * arrives as MultiPoint instead - convert it the same way actualTracks.js
 * does. Returns null if the feature has no renderable line geometry.
 * @param {object} feature - a GeoJSON Feature.
 * @returns {object|null}
 */
function toLineFeature(feature) {
  if (!feature || !feature.geometry) return null;
  const { geometry } = feature;
  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    return feature;
  }
  if (
    geometry.type === 'MultiPoint' &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length >= 2
  ) {
    return {
      ...feature,
      geometry: { type: 'LineString', coordinates: geometry.coordinates }
    };
  }
  return null;
}

/**
 * Rebuilds the source data from the full-trace cache plus the current stub
 * features. Safe to call whenever either input changes, regardless of
 * whether 'full' mode is currently visible.
 *
 * Which track ids get rendered comes from
 * MapComponent.getActiveFullTraceTrackIds() - the current stub list, plus a
 * short grace period, rather than either "only right now" (flashes tracks
 * out during a single flaky bulk-fetch cycle) or "every id ever cached"
 * (never prunes anything, so lines pile up until a full page reload).
 *
 * Per-track line-feature conversion is memoised (see trackSegmentCache.js)
 * on `this._standardLineFeaturesCache`, keyed by track id - a track whose
 * driving data (full trace, or stub while the trace is still loading)
 * hasn't changed since the last rebuild reuses its previously converted
 * line features instead of re-running toLineFeature() again.
 */
export function updateTracksFullStandardLayer() {
  if (!this.map) return;
  const source = this.map.getSource(TRACKS_FULL_STANDARD_SOURCE_ID);
  if (!source) return;

  // The bulk stub features live on the 'Tracks' entry in this.props.mapLayers
  // - NOT this.props.trackLayer, which only ever holds a single individually
  // selected track (see MapComponent.getTracksMapLayerFeatures).
  const stubFeatures = this.getTracksMapLayerFeatures
    ? this.getTracksMapLayerFeatures()
    : [];
  const cache = this.props.fullTrackTraces || {};

  const stubById = new Map();
  stubFeatures.forEach(feature => {
    const trackId = this.getTrackFeatureId(feature);
    if (trackId && !stubById.has(trackId)) {
      stubById.set(trackId, feature);
    }
  });

  const trackIds = this.getActiveFullTraceTrackIds
    ? this.getActiveFullTraceTrackIds()
    : new Set([...Object.keys(cache), ...stubById.keys()]);

  if (!this._standardLineFeaturesCache) {
    this._standardLineFeaturesCache = new Map();
  }
  const lineFeaturesCache = this._standardLineFeaturesCache;

  const features = [];
  trackIds.forEach(trackId => {
    const fullResponse = cache[trackId];
    const hasFullResponse = Array.isArray(
      fullResponse && fullResponse.features
    );

    // Full trace not loaded (yet) - fall back to the coarse stub, if this
    // track is still in the current stub list, so it isn't invisible while
    // it loads.
    const stubFeature = stubById.get(trackId);
    const trackSource = hasFullResponse ? fullResponse : stubFeature;
    if (!trackSource) return;

    const trackLineFeatures = computeWithTrackCache(
      lineFeaturesCache,
      trackId,
      trackSource,
      () =>
        hasFullResponse
          ? fullResponse.features.map(toLineFeature).filter(Boolean)
          : [toLineFeature(stubFeature)].filter(Boolean)
    );
    features.push(...trackLineFeatures);
  });

  pruneTrackCache(lineFeaturesCache, trackIds);
  source.setData({ type: 'FeatureCollection', features });
}
